import React from 'react';
import Icon from '../../../components/AppIcon';

const TransactionTimeline = ({ timeline }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'submitted': return { icon: 'Send', color: 'text-primary' };
      case 'received': return { icon: 'Inbox', color: 'text-primary' };
      case 'kyc_check': return { icon: 'UserCheck', color: 'text-warning' };
      case 'aml_screening': return { icon: 'Search', color: 'text-warning' };
      case 'sanctions_check': return { icon: 'Shield', color: 'text-warning' };
      case 'pep_check': return { icon: 'Crown', color: 'text-warning' };
      case 'risk_assessment': return { icon: 'AlertTriangle', color: 'text-warning' };
      case 'compliance_review': return { icon: 'FileCheck', color: 'text-accent' };
      case 'approved': return { icon: 'CheckCircle', color: 'text-success' };
      case 'rejected': return { icon: 'XCircle', color: 'text-error' };
      case 'processing': return { icon: 'ArrowRightLeft', color: 'text-primary' };
      case 'completed': return { icon: 'Check', color: 'text-success' };
      default: return { icon: 'Circle', color: 'text-muted-foreground' };
    }
  };

  const getStatusBg = (status, isCompleted) => {
    if (!isCompleted) return 'bg-muted/20';
    
    switch (status) {
      case 'approved': case'completed': return 'bg-success/10';
      case 'rejected': return 'bg-error/10';
      case 'compliance_review': return 'bg-accent/10';
      default: return 'bg-primary/10';
    }
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startDate, endDate) => {
    if (!endDate) return 'In progress';
    
    const diff = new Date(endDate) - new Date(startDate);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Transaction Timeline</h2>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={20} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {timeline?.filter(item => item?.completedAt)?.length}/{timeline?.length} Steps Completed
          </span>
        </div>
      </div>
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
        
        <div className="space-y-6">
          {timeline?.map((item, index) => {
            const statusConfig = getStatusIcon(item?.status);
            const isCompleted = !!item?.completedAt;
            const isLast = index === timeline?.length - 1;
            
            return (
              <div key={item?.id} className="relative flex items-start space-x-4">
                {/* Timeline Dot */}
                <div className={`relative z-10 w-12 h-12 rounded-full border-2 flex items-center justify-center ${
                  isCompleted 
                    ? `border-current ${statusConfig?.color} ${getStatusBg(item?.status, isCompleted)}` 
                    : 'border-border bg-muted/20'
                }`}>
                  <Icon 
                    name={statusConfig?.icon} 
                    size={20} 
                    className={isCompleted ? statusConfig?.color : 'text-muted-foreground'} 
                  />
                </div>
                {/* Timeline Content */}
                <div className="flex-1 min-w-0 pb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`font-medium ${isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {item?.title}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {item?.performer && (
                        <span className="text-xs text-muted-foreground">
                          by {item?.performer}
                        </span>
                      )}
                      {isCompleted && (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusConfig?.color} bg-current/10`}>
                          {item?.status?.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>

                  <p className={`text-sm mb-3 ${isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {item?.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                    <div className="flex justify-between sm:flex-col sm:justify-start">
                      <span className="text-muted-foreground">Started:</span>
                      <span className={isCompleted ? 'text-foreground' : 'text-muted-foreground'}>
                        {formatDate(item?.startedAt)}
                      </span>
                    </div>
                    
                    {isCompleted && (
                      <div className="flex justify-between sm:flex-col sm:justify-start">
                        <span className="text-muted-foreground">Completed:</span>
                        <span className="text-foreground">
                          {formatDate(item?.completedAt)}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between sm:flex-col sm:justify-start">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className={isCompleted ? 'text-foreground' : 'text-muted-foreground'}>
                        {formatDuration(item?.startedAt, item?.completedAt)}
                      </span>
                    </div>
                  </div>

                  {item?.notes && (
                    <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                      <h4 className="text-xs font-medium text-muted-foreground mb-1">Notes:</h4>
                      <p className="text-xs text-foreground">{item?.notes}</p>
                    </div>
                  )}

                  {item?.systemChecks && item?.systemChecks?.length > 0 && (
                    <div className="mt-3">
                      <h4 className="text-xs font-medium text-muted-foreground mb-2">System Checks:</h4>
                      <div className="space-y-1">
                        {item?.systemChecks?.map((check, checkIndex) => (
                          <div key={checkIndex} className="flex items-center space-x-2 text-xs">
                            <Icon 
                              name={check?.passed ? 'CheckCircle' : 'XCircle'} 
                              size={12} 
                              className={check?.passed ? 'text-success' : 'text-error'} 
                            />
                            <span className="text-foreground">{check?.name}</span>
                            {check?.value && (
                              <span className="text-muted-foreground">({check?.value})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TransactionTimeline;